import { defineStore } from 'pinia';
import { useStoreRoot } from '~/stores/abstract/root';
import {
  createPopulateString,
  parseItemInCollection,
} from '~/utils/strapiComponents/addStrapiData';
import { filterDateActiveItem, findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';
import type { IRaceData, IRaceStrapiREsponse } from '~/types/races';

export const useStoreRaces = defineStore('races', {
  state: () => ({
    races: [] as IRaceData[],
    requestPromise: null as Promise<IRaceStrapiREsponse> | null,
  }),

  getters: {
    activeRaces(state): IRaceData[] {
      const storeRoot = useStoreRoot();

      return (
        state.races
          // get races with selected language
          .filter((race) => race.locale === storeRoot.userLang)
          // filter start and finish date
          .filter(filterDateActiveItem)
          // filter by allowed groups
          .filter((race) => {
            if (!useStoreSsPlayer().isPlayerAuthorised) {
              if (race.groupFilter?.hideForGuests) {
                return false;
              }

              return true;
            }

            return findIfPassesByGroups(
              race?.groupFilter?.includedGroups || '',
              race?.groupFilter?.excludedGroups || ''
            );
          })
          // additional sorting by position
          .sort((a, b) => Number(a.position) - Number(b.position))
      );
    },
  },

  actions: {
    ADD_DATA_FROM_STRAPI(data: IRaceData[]) {
      const parsedData = data.map((race) => {
        race.minimumBet = Object.entries(race.minimumBet || []).reduce((result, item) => {
          const [key, value] = item;
          // @ts-ignore
          result[key] = value === null || value === '' ? null : parseFloat(value);
          return result;
        }, {});

        return parseItemInCollection(race);
      });

      const filteredState = this.races.filter(
        (item) => !parsedData.find(({ id }) => id && id === item.id)
      );

      this.races = [...filteredState, ...parsedData].sort(
        (a, b) => Number(a.position) - Number(b.position)
      );
    },
    async loadRacesData(): Promise<boolean> {
      if (!this.requestPromise) {
        const populateString = createPopulateString([
          'minimumBet',
          'raceTableCustom',
          'groupFilter',
          'bannerCardFront',
          'bannerCardBack',
          'progressBar',
          'providerImage',
        ]);

        this.requestPromise = strapi()
          .get('races', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        this.ADD_DATA_FROM_STRAPI(response.data);

        return true;
      } else {
        return false;
      }
    },
  },
});
